import { memo } from 'react';
import type { ReactNode } from 'react';
import type { SessionType } from '@/api/client';
import { getTitleFontSize, getContentFontSize, getTitleMarginBottom } from './fontSizeUtils';

interface SessionBubbleContentProps {
  sessionType?: SessionType | string;
  header: ReactNode;
  /** Серый текст для плейсхолдера «чтец не указан» */
  headerMuted?: boolean;
  subtitle?: string | ReactNode;
  /** Третья строка (зона «Далее:» в лишней сессии) */
  nextLine?: ReactNode;
  hasStatus?: boolean;
  /** Как legacy tracker: приглушённый серый текст (#757575) */
  isCanceled?: boolean;
  fontSize?: number;
  /** Размер шрифта для nextLine; по умолчанию fontSize */
  nextLineFontSize?: number;
  isHeaderHidden?: boolean;
  isReaderHidden?: boolean;
  isProjectHidden?: boolean;
}

function SessionBubbleContentInner({
  sessionType,
  header,
  headerMuted = false,
  subtitle,
  nextLine,
  hasStatus,
  isCanceled = false,
  fontSize = 3,
  nextLineFontSize,
  isHeaderHidden = false,
  isReaderHidden = false,
  isProjectHidden = false,
}: SessionBubbleContentProps) {
  const isNote = sessionType === 'заметка';
  const isMontage = sessionType === 'монтаж';
  const isRecordingOrEdits = sessionType === 'запись' || sessionType === 'правки';
  const hideProjectLine = (isMontage || isRecordingOrEdits) && isProjectHidden;

  const fontSizeLevel = fontSize != null ? Math.max(0, Math.min(5, fontSize)) : 3;
  const nextLineFontSizeLevel =
    nextLineFontSize != null ? Math.max(0, Math.min(5, nextLineFontSize)) : fontSizeLevel;
  const titleFontSize = getTitleFontSize(fontSizeLevel);
  const contentFontSize = getContentFontSize(fontSizeLevel);
  const nextLineContentFontSize = getContentFontSize(nextLineFontSizeLevel);
  const titleMarginBottom = getTitleMarginBottom(fontSizeLevel, isProjectHidden);

  const showHeader =
    header != null &&
    (typeof header !== 'string' || header !== '') &&
    !((isNote && isHeaderHidden) || (isRecordingOrEdits && isReaderHidden));
  const showSubtitle = subtitle != null && subtitle !== '' && !hideProjectLine;
  const showNextLine = nextLine != null && nextLine !== '';

  return (
    <div
      className={`session-bubble-content ${hasStatus ? 'session-bubble-content_has-status' : ''} ${isNote ? 'session-bubble-content_note' : ''} ${isCanceled ? 'session-bubble-content_canceled' : ''}`}
    >
      <div className="session-bubble-content__body" lang="ru">
        {showHeader && (
          <span
            className={`session-bubble-content__reader${headerMuted ? ' session-bubble-content__reader_muted' : ''}`}
            style={{ fontSize: titleFontSize, marginBottom: titleMarginBottom }}
          >
            {header}
          </span>
        )}
        {showSubtitle && (
          <span
            className={`session-bubble-content__title ${isNote ? 'session-bubble-content__title_note' : ''}`}
            style={{
              fontSize: contentFontSize,
              ...(showNextLine ? { marginBottom: titleMarginBottom } : {}),
            }}
          >
            {subtitle}
          </span>
        )}
        {showNextLine && (
          <span className="session-bubble__split-text" style={{ fontSize: nextLineContentFontSize }}>
            {nextLine}
          </span>
        )}
      </div>
    </div>
  );
}

export const SessionBubbleContent = memo(SessionBubbleContentInner);
